import { useState, useEffect } from 'react'
import { X, PlayCircle } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { getWelcomeVideo, type TutorialVideo } from '../services/tutorialVideoService'

interface WelcomeVideoModalProps {
  isOpen: boolean
  onClose: () => void
}

// フォールバック用のデフォルト動画
const DEFAULT_VIDEO = {
  youtube_id: '16AEBCcUL4g',
  title: 'TaStacK 基本操作編',
}

export default function WelcomeVideoModal({ isOpen, onClose }: WelcomeVideoModalProps) {
  const navigate = useNavigate()
  const [video, setVideo] = useState<TutorialVideo | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (isOpen) {
      loadWelcomeVideo()
    }
  }, [isOpen])

  const loadWelcomeVideo = async () => {
    try {
      setLoading(true)
      const data = await getWelcomeVideo()
      setVideo(data)
    } catch (error) {
      console.error('Error loading welcome video:', error)
    } finally {
      setLoading(false)
    }
  }

  if (!isOpen) return null

  // 動画情報（DBから取得できない場合はデフォルトを使用）
  const youtubeId = video?.youtube_id || DEFAULT_VIDEO.youtube_id
  const videoTitle = video?.title || DEFAULT_VIDEO.title

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 animate-fade-slide-in"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose()
        }
      }}
    >
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto relative shadow-2xl animate-scale-in">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between rounded-t-2xl">
          <div className="flex items-center gap-2">
            <PlayCircle className="h-6 w-6 text-indigo-600" />
            <h2 className="text-xl font-bold text-gray-900">TaStacKへようこそ！</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors tap-highlight"
            aria-label="閉じる"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <p className="text-gray-600 mb-4 text-center">
            まずは基本操作を動画でご確認ください
          </p>

          {/* Video Embed */}
          {loading ? (
            <div className="relative w-full mb-6" style={{ paddingBottom: '56.25%' }}>
              <div className="absolute inset-0 bg-gray-100 rounded-xl animate-pulse flex items-center justify-center">
                <PlayCircle className="h-12 w-12 text-gray-300" />
              </div>
            </div>
          ) : (
            <div className="relative w-full mb-6" style={{ paddingBottom: '56.25%' }}>
              <iframe
                className="absolute top-0 left-0 w-full h-full rounded-xl shadow-lg"
                src={`https://www.youtube-nocookie.com/embed/${youtubeId}`}
                title={videoTitle}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                referrerPolicy="strict-origin-when-cross-origin"
                allowFullScreen
              />
            </div>
          )}

          {/* Link to all videos */}
          <div className="text-center border-t border-gray-200 pt-4">
            <button
              onClick={() => {
                onClose()
                navigate('/help/videos')
              }}
              className="inline-flex items-center gap-2 text-indigo-600 hover:text-indigo-800 font-medium transition-colors tap-highlight"
            >
              <PlayCircle className="h-5 w-5" />
              <span>その他の動画はこちら</span>
            </button>
          </div>

          {/* Close Button */}
          <div className="mt-6 text-center">
            <button
              onClick={onClose}
              className="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium tap-highlight"
            >
              閉じる
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
